import {
  allFittedComponentsQuery,
  useCreateFittedComponentMutation,
} from "@common/api/aircraft"
import { fittableStockQuery } from "@common/api/part-balances"
import { currentUserQuery } from "@common/api/users"
import type { UserPublicMe } from "@common/client"
import { InlineTextField } from "@common/components/form/fields/inline"
import { Button } from "@common/components/ui/button"
import { Form } from "@common/components/ui/form"
import {
  SegmentedControl,
  SegmentedItem,
} from "@common/components/ui/segmented-control"
import { useCurrentUser } from "@common/hooks/use-permissions"
import { useSharedForm } from "@common/hooks/use-shared-form"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import {
  DetailPane,
  DetailPanelActions,
  DetailPanelFields,
} from "@/components/layout/detail-panel"
import { PaneSection, PaneSections } from "@/components/page/pane"
import useCustomToast from "@/hooks/use-custom-toast"
import { FittingSection } from "../-components/fitted-components/fitting-section"
import { StockPicker } from "../-components/fitted-components/stock-picker"
import { componentLabel } from "../-lib/fitted-components"
import {
  emptyNewComponent,
  type NewComponentFormInput,
  newComponentPayload,
  newComponentSchema,
  PART_SOURCES,
  type PartSource,
  partSourceLabel,
} from "../-lib/fitting-form"

const STOCK_PERMISSIONS = [
  "part:read",
  "part_balance:read",
  "part_balance:update",
]

function canPickStock(me: UserPublicMe | undefined): boolean {
  return STOCK_PERMISSIONS.every((permission) => hasPermission(me, permission))
}

export const Route = createFileRoute(
  "/_layout/aircraft_/$aircraftId/fitted-components/new",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "fitted_components:create")) {
      throw redirect({ to: "/aircraft/$aircraftId", params, search })
    }
  },
  loader: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (canPickStock(me)) {
      await queryClient
        .ensureQueryData(fittableStockQuery(""))
        .catch(() => undefined)
    }
  },
  component: NewComponentPane,
})

function NewComponentPane(): React.ReactElement {
  const { aircraftId } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  const showToast = useCustomToast()
  const mutation = useCreateFittedComponentMutation()
  const { data } = useQuery(allFittedComponentsQuery(aircraftId))
  const canPick = useCanPickStock()
  const { data: stock } = useQuery({
    ...fittableStockQuery(""),
    enabled: canPick,
  })
  const startFrom: PartSource =
    canPick && (stock?.count ?? 0) > 0 ? "stock" : "manual"
  const form = useSharedForm<NewComponentFormInput>({
    resolver: zodResolver(newComponentSchema),
    defaultValues: emptyNewComponent(startFrom),
    criteriaMode: "all",
    mode: "onBlur",
  })
  const close = (opened?: string) =>
    navigate({
      to: "/aircraft/$aircraftId",
      params: { aircraftId },
      search: (previous) => ({
        ...previous,
        tab: "fitted-components" as const,
        fitted_item: opened,
      }),
    })

  const source = form.watch("source")
  const switchTo = (next: PartSource) => {
    form.setValue("source", next, { shouldDirty: true })
    form.setValue("part_balance_id", null)
    form.clearErrors()
  }

  const submit = (values: NewComponentFormInput) =>
    mutation.mutate(
      { fittedComponentCreate: newComponentPayload(values, aircraftId) },
      {
        onSuccess: (created) => {
          const label = componentLabel(created)
          const message =
            values.source === "stock"
              ? `${label} fitted and issued from stock.`
              : `${label} added.`
          showToast("Created", message, "success")
          close(created.id)
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <DetailPane title="New component" onClose={() => close()}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex h-full flex-col"
        >
          <DetailPanelFields>
            <PaneSections>
              <PaneSection<NewComponentFormInput>
                title="Part"
                fields={["part_balance_id", "part_number", "serial_number"]}
                action={
                  canPick && (
                    <SegmentedControl
                      value={source}
                      onValueChange={(next) => {
                        const picked = PART_SOURCES.find(
                          (option) => option === next,
                        )
                        if (picked) switchTo(picked)
                      }}
                    >
                      {PART_SOURCES.map((option) => (
                        <SegmentedItem key={option} value={option}>
                          {partSourceLabel(option)}
                        </SegmentedItem>
                      ))}
                    </SegmentedControl>
                  )
                }
              >
                {source === "stock" ? (
                  <StockPicker onManual={() => switchTo("manual")} />
                ) : (
                  <ManualPart />
                )}
              </PaneSection>
              <FittingSection
                component={null}
                components={data?.data ?? []}
                editable
              />
            </PaneSections>
          </DetailPanelFields>
          <DetailPanelActions
            dirty={form.formState.isDirty}
            pending={mutation.isPending}
            onReset={() => form.reset()}
            alwaysShowSave
          >
            <Button type="button" variant="outline" onClick={() => close()}>
              Cancel
            </Button>
          </DetailPanelActions>
        </form>
      </Form>
    </DetailPane>
  )
}

function useCanPickStock(): boolean {
  return canPickStock(useCurrentUser())
}

function ManualPart(): React.ReactElement {
  return (
    <div className="min-w-0 space-y-1">
      <InlineTextField<NewComponentFormInput>
        name="part_number"
        label="Part number"
      />
      <InlineTextField<NewComponentFormInput>
        name="serial_number"
        label="Serial number"
      />
      <InlineTextField<NewComponentFormInput>
        name="part_description"
        label="Description"
      />
    </div>
  )
}
